import { Box3, Group, MathUtils, Mesh, MeshPhysicalMaterial, Object3D, Vector3 } from "three";
import type { EnvironmentDecorItemConfig, SceneProps } from "./SceneProps";

const tmpBox = new Box3();
const tmpSize = new Vector3();
const tmpCenter = new Vector3();
const tmpOffset = new Vector3();
const DEFAULT_SCALE = { x: 1, y: 1, z: 1 };

function fitObjectToTargetSize(object: Object3D, targetSize: number) {
  object.updateMatrixWorld(true);
  tmpBox.setFromObject(object);
  tmpBox.getSize(tmpSize);

  const maxDimension = Math.max(tmpSize.x, tmpSize.y, tmpSize.z);
  if (maxDimension > 0) {
    object.scale.multiplyScalar(targetSize / maxDimension);
  }

  object.updateMatrixWorld(true);
  tmpBox.setFromObject(object);
  tmpBox.getCenter(tmpCenter);

  tmpOffset.set(-tmpCenter.x, -tmpBox.min.y, -tmpCenter.z);
  object.position.add(tmpOffset);
}

function markAsDecor(object: Object3D) {
  object.traverse((child) => {
    child.userData.type = "environmentDecor";
    child.userData.collectable = false;
  });
}

function getDecorSignature(config: EnvironmentDecorItemConfig[] | undefined) {
  return (
    config?.map((item) => `${item.model.key}:${item.targetSize}`).join("|") ??
    ""
  );
}

function applyGroupTransform(group: Group, item: EnvironmentDecorItemConfig) {
  const scale = item.scale ?? DEFAULT_SCALE;
  group.position.set(item.position.x, item.position.y, item.position.z);
  group.rotation.set(item.rotation.x, item.rotation.y, item.rotation.z);
  group.scale.set(scale.x, scale.y, scale.z);
}

export function createEnvironmentDecorLayer(
  props: Pick<SceneProps, "models" | "scene" | "environmentDecor">,
) {
  const groups: Group[] = [];
  let signature = "";
  let revealProgress = 1;
  let currentConfig: EnvironmentDecorItemConfig[] | undefined =
    props.environmentDecor;

  const applyRevealTransform = (group: Group) => {
    const progress = Math.max(0, Math.min(1, revealProgress));
    group.visible = progress > 0.02;
    group.scale.multiplyScalar(MathUtils.lerp(0.88, 1, progress));
  };

  const clear = () => {
    groups.forEach((group) => group.removeFromParent());
    groups.length = 0;
    signature = "";
  };

  const build = (config: EnvironmentDecorItemConfig[] | undefined) => {
    clear();
    currentConfig = config;

    if (!config?.length) return;

    signature = getDecorSignature(config);

    config.forEach((item, index) => {
      const model = props.models[item.model.key];
      const object = model?.getModel();
      if (!object) return;

      object.traverse((child) => {
        if (child instanceof Mesh) {
          child.material = new MeshPhysicalMaterial({
            map: child.material.map,
            color: child.material.color,
            roughness: 0.7,
            metalness: 0.1,
          })
        }
      });

      object.name = `environment-decor-object-${index}`;
      markAsDecor(object);
      fitObjectToTargetSize(object, item.targetSize);

      const group = new Group();
      group.name = `environment-decor-${index}`;
      group.userData.type = "environmentDecor";
      group.userData.collectable = false;
      applyGroupTransform(group, item);
      applyRevealTransform(group);
      group.add(object);

      props.scene.add(group);
      groups.push(group);
    });
  };

  const setItems = (config: EnvironmentDecorItemConfig[] | undefined) => {
    currentConfig = config;
    const nextSignature = getDecorSignature(config);
    if (nextSignature !== signature) {
      build(config);
      return;
    }

    config?.forEach((item, index) => {
      const group = groups[index];
      if (group) {
        applyGroupTransform(group, item);
        applyRevealTransform(group);
      }
    });
  };

  build(props.environmentDecor);

  return {
    setItems,
    setRevealProgress: (progress: number) => {
      revealProgress = progress;
      groups.forEach((group, index) => {
        const item = currentConfig?.[index];
        if (item) {
          applyGroupTransform(group, item);
          applyRevealTransform(group);
        }
      });
    },
    dispose: clear,
  };
}
